import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { storyApi, settingsApi, slideApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Spinner } from '../components/ui/spinner';
import { useToast } from '../contexts/ToastContext';
import { ChevronLeft, Save, Plus, Trash2, Edit2, MoveUp, MoveDown, AlertCircle, CheckCircle, Upload, X, Copy } from 'lucide-react';
import { getImageUrl } from '../utils/imageUrl';
import { CategorySelect } from '../components/CategorySelect';
import { CategoryCreateModal } from '../components/CategoryCreateModal';
import { isFeatureEnabled } from '../config/features';
import { withSuccessToast, successMessages } from '../utils/apiHelpers';


interface Category {
  _id: string;
  name: string;
}

interface Subcategory {
  _id: string;
  category_id: string;
  name: string;
}

interface Tag {
  id: number;
  name: string;
}

interface Slide {
  id?: number;
  image_path?: string;
  narration: string;
  position: number;
  file?: File;
  preview?: string;
  isComplete?: boolean;
}

interface StoryData {
  title: string;
  one_line_brief: string;
  category_id: string;
  subcat_id: string;
  tags: string[];
  thumbnail_url?: string;
}

const performAutoExport = async () => {
  const autoExportEnabled = localStorage.getItem('autoExportEnabled');
  // Check if auto-export is enabled (default true if not set)
  if (autoExportEnabled === 'true' || autoExportEnabled === null) {
    try {
      const response = await storyApi.exportUserData();
      const data = response.data.data;

      // Get local datetime string and format it for filename
      const now = new Date();
      const localDateTime = now.toLocaleString('sv-SE').replace(/[:\s]/g, '-');
      const filename = `stories-export-${localDateTime}.json`;

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      console.log('Auto-export completed successfully');
    } catch (error) {
      console.error('Auto-export failed:', error);
    }
  } else {
    console.log('Auto-export is disabled');
  }
};

export default function StoryEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>('');

  const [storyData, setStoryData] = useState<StoryData>({
    title: '',
    one_line_brief: '',
    category_id: '',
    subcat_id: '',
    tags: []
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [tagInput, setTagInput] = useState('');

  const [slides, setSlides] = useState<Slide[]>([]);
  const [editingSlide, setEditingSlide] = useState<number | null>(null);

  // Modal states for inline creation
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);

  // Feature flags
  const enableInlineCreation = isFeatureEnabled('INLINE_CATEGORY_CREATION');

  useEffect(() => {
    loadInitialData();
  }, [id]);

  useEffect(() => {
    if (storyData.category_id) {
      // Clear subcategories first to ensure clean state
      setSubcategories([]);
      loadSubcategories(storyData.category_id);
    } else {
      // Clear subcategories when no category is selected
      setSubcategories([]);
    }
  }, [storyData.category_id]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, tagsRes] = await Promise.all([
        settingsApi.getCategories(),
        settingsApi.getTags()
      ]);

      // New API structure: { data: {...}, success, message }
      setCategories(categoriesRes.data.data || []);
      setAvailableTags(tagsRes.data.data || []);

      if (id) {
        const storyRes = await storyApi.getStory(id);
        console.log('Story response:', storyRes.data);
        
        // New API structure: { data: { story: {...}, slides: [] }, success, message }
        if (!storyRes.data.data) {
          throw new Error('Invalid API response structure');
        }
        
        const { story, slides } = storyRes.data.data;

        setStoryData({
          title: story.title,
          one_line_brief: story.one_line_brief || '',
          category_id: story.category_id || '',
          subcat_id: story.subcat_id || '',
          tags: story.tags?.filter((tag: string) => tag) || [],
          thumbnail_url: story.thumbnail_url
        });
        setStoryBreakPreview(getImageUrl(story.breakCard?.image))
        
        // Set thumbnail preview if exists
        if (story.thumbnail_url) {
          setThumbnailPreview(getImageUrl(story.thumbnail_url));
        }

        // Load subcategories if story has a category
        if (story.category_id) {
          await loadSubcategories(story.category_id);
        }

        // Slides are already included in the response
        setSlides(slides.map((slide: any) => ({
          id: slide._id || slide.id,
          image_path: slide.image_url || slide.image_path,
          narration: slide.narration || '',
          position: slide.position,
          isComplete: !!(slide.image_url || slide.image_path) && slide.narration
        })));
        
        // If editing existing story with slides, show slides step
        if (slides.length > 0) {
          setCurrentStep(2);
        }
      }
    } catch (error: any) {
      console.error('Failed to load data:', error);
      console.error('Error details:', error.response?.data);
      toast({
        title: 'Error',
        description: 'Failed to load story data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSubcategories = async (categoryId: string) => {
    try {
      const res = await settingsApi.getSubcategories(categoryId);
      // New API structure: { data: {...}, success, message }
      setSubcategories(res.data.data || []);
    } catch (error) {
      console.error('Failed to load subcategories:', error);
    }
  };

  const handleCategoryCreated = (category: Category) => {
    setCategories([...categories, category]);
    setStoryData({ ...storyData, category_id: category._id });
    // Load subcategories for the new category
    loadSubcategories(category._id);
  };

  const handleSubcategoryCreated = (subcategory: Subcategory) => {
    setSubcategories([...subcategories, subcategory]);
    setStoryData({ ...storyData, subcat_id: subcategory._id });
  };

  const handleSubcategoryModalCreated = (item: { _id: string; name: string }) => {
    const subcategory: Subcategory = {
      _id: item._id,
      name: item.name,
      category_id: storyData.category_id || ''
    };
    handleSubcategoryCreated(subcategory);
  };

  const handleThumbnailSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveMetadata = async () => {
    if (!storyData.title) {
      toast({
        title: 'Validation Error',
        description: 'Title is required',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', storyData.title);
      formData.append('one_line_brief', storyData.one_line_brief);
      if (storyData.category_id) formData.append('category_id', storyData.category_id);
      if (storyData.subcat_id) formData.append('subcat_id', storyData.subcat_id);
      storyData.tags.forEach(tag => formData.append('tags', tag));
      
      if (thumbnailFile) {
        formData.append('image', thumbnailFile);
      }

      if (id) {
        await withSuccessToast(
          storyApi.updateStory(id, formData),
          successMessages.storyUpdated
        );
      } else {
        const res = await withSuccessToast(
          storyApi.createStory(formData),
          successMessages.storyCreated
        );
        // New API structure: { data: {...}, success, message }
        const newId = res.data.data._id;
        navigate(`/stories/${newId}/edit`, { replace: true });
      }

      setCurrentStep(2);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to save story',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput && !storyData.tags.includes(tagInput)) {
      setStoryData({ ...storyData, tags: [...storyData.tags, tagInput] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setStoryData({
      ...storyData,
      tags: storyData.tags.filter(t => t !== tag)
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const newSlide: Slide = {
          file,
          preview: reader.result as string,
          narration: '',
          position: slides.length,
          isComplete: false
        };
        setSlides([...slides, newSlide]);
        setEditingSlide(slides.length);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddEmptySlide = () => {
    const newSlide: Slide = {
      narration: '',
      position: slides.length,
      isComplete: false
    };
    setSlides([...slides, newSlide]);
    setEditingSlide(slides.length);
  };

  const handleSaveSlide = async (index: number) => {
    if (!id) return;

    const slide = slides[index];
    setSaving(true);

    try {
      if (slide.id) {
        // Update existing slide
        const formData = new FormData();
        if (slide.file) {
          formData.append('image', slide.file);
        }
        formData.append('narration', slide.narration || '');
        formData.append('position', slide.position.toString());

        const res = await slideApi.updateSlide(id!, slide.id.toString(), formData);
        // New API structure: { data: {...}, success, message }
        slides[index] = {
          ...slide,
          ...res.data.data,
          isComplete: !!(res.data.data.image_path && res.data.data.narration)
        };
        setSlides([...slides]);
      } else {
        // Create new slide
        const formData = new FormData();
        if (slide.file) {
          formData.append('image', slide.file);
        }
        formData.append('narration', slide.narration || '');
        formData.append('position', slide.position.toString());

        const res = await storyApi.createSlide(id, formData);
        // New API structure: { data: {...}, success, message }
        slides[index] = {
          ...slide,
          id: res.data.data._id,
          image_path: res.data.data.image_path,
          isComplete: !!(res.data.data.image_path && res.data.data.narration)
        };
        setSlides([...slides]);
      }

      setEditingSlide(null);
      toast({
        title: 'Success',
        description: 'Slide saved successfully'
      });
    } catch (error: any) {
        console.log('error', error)
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to save slide',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSlide = async (index: number) => {
    const slide = slides[index];

    if (slide.id && id) {
      if (!window.confirm('Are you sure you want to delete this slide?')) return;

      try {
        await slideApi.deleteSlide(id, slide.id.toString());
        toast({
          title: 'Success',
          description: 'Slide deleted successfully'
        });
      } catch (error) {
          console.log('error', error)
        toast({
          title: 'Error',
          description: 'Failed to delete slide',
          variant: 'destructive'
        });
        return;
      }
    }

    const newSlides = slides.filter((_, i) => i !== index);
    newSlides.forEach((slide, i) => {
      slide.position = i;
    });
    setSlides(newSlides);
  };

  const handleMoveSlide = async (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= slides.length) return;

    const newSlides = [...slides];
    [newSlides[index], newSlides[newIndex]] = [newSlides[newIndex], newSlides[index]];

    // Update positions to be 0-indexed
    newSlides.forEach((slide, i) => {
      slide.position = i;
    });

    setSlides(newSlides);

    // Update position for the slide that was moved (send the original slide ID that was clicked)
    const clickedSlide = slides[index]; // The slide whose button was clicked
    const movedSlide = newSlides[newIndex]; // Where it ended up
    if (id && clickedSlide.id) {
      try {
        await slideApi.updateSlidePosition(id!, clickedSlide.id!.toString(), movedSlide.position);
      } catch (error) {
        console.log('error',error)
        toast({
          title: 'Error',
          description: 'Failed to reorder slides',
          variant: 'destructive'
        });
      }
    }
  };

  const handleDuplicateSlide = async (index: number) => {
    if (!id) return;

    // Check if there are any unsaved slides
    const hasUnsavedSlides = slides.some(slide => !slide.id);
    if (hasUnsavedSlides) {
      toast({
        title: 'Warning',
        description: 'Please save all slides before duplicating',
        variant: 'destructive'
      });
      return;
    }

    const slide = slides[index];
    if (!slide.id) return;

    setSaving(true);
    try {
      await slideApi.duplicateSlide(id, slide.id.toString(), index);

      // Refetch all data after duplication
      await loadInitialData();

      toast({
        title: 'Success',
        description: 'Slide duplicated successfully'
      });
    } catch (error) {
      console.log('error', error)
      toast({
        title: 'Error',
        description: 'Failed to duplicate slide',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };


   // story break 

const [storyBreakPreview, setStoryBreakPreview] = useState(""); // URL or object URL

const fileInputRef = useRef<HTMLInputElement | null>(null);


const openFilePicker = () => {
  fileInputRef.current?.click();
};

const handleStoryBreakFileSelect = async (
  e: React.ChangeEvent<HTMLInputElement>
) => {
  const file = e.target.files?.[0];
  if (!file || !id) return;

  setSaving(true);

  // Allow selecting the same file again
  e.target.value = "";

  const formData = new FormData();
  formData.append("image", file);
  formData.append("_id", id);

  try {
    await storyApi.addStoryBreakToSlides(id, formData);

    // Remove previous preview if it was a blob URL
    if (storyBreakPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(storyBreakPreview);
    }

    // Show preview only after successful upload
    const preview = URL.createObjectURL(file);
    setStoryBreakPreview(preview);

    toast({
      title: "Success",
      description: "Story break image uploaded successfully.",
    });
  } catch (error) {
    console.error(error);

    toast({
      title: "Error",
      description: "Failed to upload story break image.",
      variant: "destructive",
    });
  } finally {
    setSaving(false);
  }
};

const removeStoryBreak = async () => {
  if (!id) return;

  setSaving(true);

  try {
    const formData = new FormData();
    formData.append("_id", id);
    formData.append("deleteBreakCard", "true");

    await storyApi.addStoryBreakToSlides(id, formData);

    if (storyBreakPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(storyBreakPreview);
    }

    setStoryBreakPreview("");

    toast({
      title: "Success",
      description: "Story break removed successfully.",
    });
  } catch (error) {
    console.error(error);

    toast({
      title: "Error",
      description: "Failed to remove story break.",
      variant: "destructive",
    });
  } finally {
    setSaving(false);
  }
};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card shadow-sm border-b">
        <div className="max-w-4xl mx-auto mobile-padding">
          <div className="flex justify-between items-center py-4">
            <h1 className="heading-responsive font-bold text-foreground">
              {id ? "Edit Story" : "Create New Story"}
            </h1>
            <Button variant="ghost" onClick={() => navigate("/dashboard")}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-6 mobile-padding">
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-center space-x-2 sm:space-x-4 overflow-x-auto hide-scrollbar">
            <div
              className={`flex items-center ${currentStep >= 1 ? "text-primary" : "text-muted-foreground"}`}
            >
              <div
                className={`rounded-full h-8 w-8 flex items-center justify-center border-2 ${
                  currentStep >= 1
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-muted-foreground"
                }`}
              >
                1
              </div>
              <span className="ml-2 font-medium text-sm sm:text-base whitespace-nowrap">
                Story Details
              </span>
            </div>
            <div className="w-8 sm:w-16 h-0.5 bg-border flex-shrink-0" />
            <div
              className={`flex items-center ${currentStep >= 2 ? "text-primary" : "text-muted-foreground"}`}
            >
              <div
                className={`rounded-full h-8 w-8 flex items-center justify-center border-2 ${
                  currentStep >= 2
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-muted-foreground"
                }`}
              >
                2
              </div>
              <span className="ml-2 font-medium text-sm sm:text-base whitespace-nowrap">
                Add Slides
              </span>
            </div>
            <div className="w-8 sm:w-16 h-0.5 bg-border flex-shrink-0" />
            <div
              className={`flex items-center ${currentStep >= 3 ? "text-primary" : "text-muted-foreground"}`}
            >
              <div
                className={`rounded-full h-8 w-8 flex items-center justify-center border-2 ${
                  currentStep >= 3
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-muted-foreground"
                }`}
              >
                3
              </div>
              <span className="ml-2 font-medium text-sm sm:text-base whitespace-nowrap">
                Story Break
              </span>
            </div>
          </div>
        </div>

        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Story Details</CardTitle>
              <CardDescription>
                Add basic information about your story
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={storyData.title}
                  onChange={(e) =>
                    setStoryData({ ...storyData, title: e.target.value })
                  }
                  placeholder="Enter story title"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="brief">One Line Brief</Label>
                <Textarea
                  id="brief"
                  value={storyData.one_line_brief}
                  onChange={(e) =>
                    setStoryData({
                      ...storyData,
                      one_line_brief: e.target.value,
                    })
                  }
                  placeholder="Brief description of your story"
                  className="mt-1"
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="thumbnail">Thumbnail Image</Label>
                <div className="mt-1 space-y-2">
                  {thumbnailPreview && (
                    <div className="relative w-32 h-32">
                      <img
                        src={thumbnailPreview}
                        alt="Thumbnail preview"
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setThumbnailFile(null);
                          setThumbnailPreview("");
                        }}
                        className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  <div>
                    <Input
                      id="thumbnail"
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailSelect}
                      className="hidden"
                    />
                    <Label
                      htmlFor="thumbnail"
                      className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
                    >
                      <Upload className="h-4 w-4" />
                      {thumbnailPreview
                        ? "Change Thumbnail"
                        : "Upload Thumbnail"}
                    </Label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category</Label>
                  {enableInlineCreation ? (
                    <CategorySelect
                      id="category"
                      value={storyData.category_id}
                      onChange={(value) =>
                        setStoryData({
                          ...storyData,
                          category_id: value,
                          subcat_id: "",
                        })
                      }
                      categories={categories}
                      onAddNew={() => setShowCategoryModal(true)}
                      placeholder="Select a category"
                      showAddOption={true}
                      className="mt-1"
                    />
                  ) : (
                    <Select
                      id="category"
                      value={storyData.category_id}
                      onChange={(e) =>
                        setStoryData({
                          ...storyData,
                          category_id: e.target.value,
                          subcat_id: "",
                        })
                      }
                      className="mt-1"
                    >
                      <option value="">Select a category</option>
                      {categories.map((cat) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </div>

                <div>
                  <Label htmlFor="subcategory">Subcategory</Label>
                  {enableInlineCreation ? (
                    <CategorySelect
                      id="subcategory"
                      value={storyData.subcat_id}
                      onChange={(value) =>
                        setStoryData({ ...storyData, subcat_id: value })
                      }
                      categories={subcategories}
                      onAddNew={() => setShowSubcategoryModal(true)}
                      placeholder="Select a subcategory"
                      disabled={!storyData.category_id}
                      showAddOption={!!storyData.category_id}
                    />
                  ) : (
                    <Select
                      id="subcategory"
                      value={storyData.subcat_id}
                      onChange={(e) =>
                        setStoryData({
                          ...storyData,
                          subcat_id: e.target.value,
                        })
                      }
                      className="mt-1"
                      disabled={!storyData.category_id}
                    >
                      <option value="">Select a subcategory</option>
                      {subcategories.map((sub) => (
                        <option key={sub._id} value={sub._id}>
                          {sub.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="tags">Tags</Label>
                <div className="mt-1 space-y-2">
                  <div className="flex gap-2">
                    <Input
                      id="tags"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddTag())
                      }
                      placeholder="Add tags..."
                      list="tag-suggestions"
                    />
                    <Button onClick={handleAddTag} type="button">
                      Add
                    </Button>
                  </div>
                  <datalist id="tag-suggestions">
                    {availableTags.map((tag) => (
                      <option key={tag.id} value={tag.name} />
                    ))}
                  </datalist>
                  <div className="flex flex-wrap gap-2">
                    {storyData.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-sm"
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          className="hover:text-destructive"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveMetadata}
                  disabled={saving || !storyData.title}
                >
                  {saving ? (
                    <Spinner size="sm" className="mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save & Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Story Slides</CardTitle>
              <CardDescription>
                <div className="space-y-1">
                  <p>Add images and narration for your story</p>
                  {slides.length > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium">Progress:</span>
                      <span
                        className={
                          slides.filter(
                            (s) =>
                              !!(s.image_path || s.preview) && !!s.narration,
                          ).length === slides.length
                            ? "text-green-600"
                            : "text-yellow-600"
                        }
                      >
                        {
                          slides.filter(
                            (s) =>
                              !!(s.image_path || s.preview) && !!s.narration,
                          ).length
                        }{" "}
                        of {slides.length} slides complete
                      </span>
                    </div>
                  )}
                </div>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {slides.length === 0 && (
                  <div className="text-center py-8 px-4 bg-muted/50 rounded-lg">
                    <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <h3 className="text-lg font-medium mb-2">No slides yet</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Start building your story by adding your first slide
                    </p>
                    <p className="text-xs text-muted-foreground">
                      You can upload an image or start with text
                    </p>
                  </div>
                )}
                {slides.map((slide, index) => {
                  const hasImage = !!(slide.image_path || slide.preview);
                  const hasNarration = !!slide.narration;
                  const isSlideComplete = hasImage && hasNarration;

                  return (
                    <div
                      key={index}
                      className={`border rounded-lg p-4 ${!isSlideComplete ? "border-yellow-500" : "border-green-500"}`}
                    >
                      {editingSlide === index ? (
                        // Edit Mode - Side by Side Layout
                        <div className="space-y-4">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-medium text-sm">
                              Editing Slide {index + 1}
                            </h3>
                            <div className="flex items-center gap-4 text-xs">
                              <span
                                className={`flex items-center gap-1 ${hasImage ? "text-green-600" : "text-yellow-600"}`}
                              >
                                {hasImage ? (
                                  <CheckCircle className="h-3 w-3" />
                                ) : (
                                  <AlertCircle className="h-3 w-3" />
                                )}
                                Image
                              </span>
                              <span
                                className={`flex items-center gap-1 ${hasNarration ? "text-green-600" : "text-yellow-600"}`}
                              >
                                {hasNarration ? (
                                  <CheckCircle className="h-3 w-3" />
                                ) : (
                                  <AlertCircle className="h-3 w-3" />
                                )}
                                Narration
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Left Side - Image Upload */}
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">
                                Image
                              </Label>
                              {slide.preview || slide.image_path ? (
                                <div className="relative">
                                  <img
                                    src={
                                      slide.preview ||
                                      getImageUrl(slide.image_path)
                                    }
                                    alt={`Slide ${index + 1}`}
                                    className="w-full aspect-[393/620] object-cover rounded-md"
                                  />
                                  <Label
                                    htmlFor={`slide-image-${index}`}
                                    className="absolute bottom-2 right-2 bg-primary text-primary-foreground px-3 py-1 rounded-md text-xs cursor-pointer hover:bg-primary/90"
                                  >
                                    Change Image
                                  </Label>
                                  <Input
                                    id={`slide-image-${index}`}
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                          const newSlides = [...slides];
                                          newSlides[index].file = file;
                                          newSlides[index].preview =
                                            reader.result as string;
                                          setSlides(newSlides);
                                        };
                                        reader.readAsDataURL(file);
                                      }
                                    }}
                                    className="hidden"
                                  />
                                </div>
                              ) : (
                                <div className="border-2 border-dashed rounded-md aspect-[393/620] flex items-center justify-center">
                                  <Input
                                    id={`slide-image-${index}`}
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                          const newSlides = [...slides];
                                          newSlides[index].file = file;
                                          newSlides[index].preview =
                                            reader.result as string;
                                          setSlides(newSlides);
                                        };
                                        reader.readAsDataURL(file);
                                      }
                                    }}
                                    className="hidden"
                                  />
                                  <Label
                                    htmlFor={`slide-image-${index}`}
                                    className="cursor-pointer text-center p-4"
                                  >
                                    <Plus className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                                    <p className="text-sm text-muted-foreground">
                                      Click to upload image
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Recommended: 393×620px
                                    </p>
                                  </Label>
                                </div>
                              )}
                            </div>

                            {/* Right Side - Narration */}
                            <div className="space-y-2">
                              <div className="flex justify-between items-center">
                                <Label className="text-sm font-medium">
                                  Narration{" "}
                                </Label>
                                <span
                                  className={`text-xs ${(slide.narration || "").length > 200 ? "text-destructive font-semibold" : "text-muted-foreground"}`}
                                >
                                  {(slide.narration || "").length}/200
                                  characters
                                </span>
                              </div>
                              <Textarea
                                value={slide.narration || ""}
                                onChange={(e) => {
                                  const newSlides = [...slides];
                                  newSlides[index].narration =
                                    e.target.value.slice(0, 200);
                                  setSlides(newSlides);
                                }}
                                placeholder="Enter narration for this slide..."
                                rows={8}
                                className="resize-none"
                                maxLength={200}
                              />
                              <p style={{ fontSize: 12 }}>
                                <i>
                                  <b>Formatting Guide:</b>
                                </i>
                                <br />
                                *text* = <b>Bold</b> | _text_ = <i>Italic</i> |
                                *_text_* ={" "}
                                <b>
                                  <i>Bold Italic</i>
                                </b>
                              </p>
                              {/* {!slide.narration && (
                                <p className="text-xs text-yellow-600">Narration is required for publishing</p>
                              )} */}
                            </div>

                            {/* <div style={{ marginTop: 10 }}>
                              <Label className="text-sm font-medium" style={{ marginBottom: 20 }}>Narration</Label>
                              <RichTextEditor
                                value={slide.narration || ""}
                                max={200}
                                placeholder="Enter narration for this slide..."
                                onChange={(val) => {
                                  const newSlides = [...slides];
                                  newSlides[index].narration = val;
                                  setSlides(newSlides);
                                }}
                              />

                              {!slide.narration && (
                                <p className="text-xs text-yellow-600">Narration is required for publishing</p>
                              )}
                            </div> */}
                          </div>

                          <div className="flex justify-end gap-2 pt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingSlide(null)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleSaveSlide(index)}
                              disabled={saving}
                            >
                              {saving ? (
                                <Spinner size="sm" className="mr-2" />
                              ) : null}
                              Save Slide
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="flex-shrink-0 mx-auto sm:mx-0">
                            {slide.preview || slide.image_path ? (
                              <img
                                src={
                                  slide.preview || getImageUrl(slide.image_path)
                                }
                                alt={`Slide ${index + 1}`}
                                className="w-full sm:w-40 aspect-[393/620] object-cover rounded-md"
                              />
                            ) : (
                              <div className="w-full sm:w-40 aspect-[393/620] bg-muted rounded-md flex items-center justify-center">
                                <AlertCircle className="h-8 w-8 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="text-sm font-medium">
                                Slide {index + 1}
                              </p>
                              {isSlideComplete ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <div className="flex items-center gap-2">
                                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                                  <span className="text-xs text-yellow-600">
                                    Missing:{" "}
                                    {[
                                      !hasImage && "image",
                                      !hasNarration && "narration",
                                    ]
                                      .filter(Boolean)
                                      .join(", ")}
                                  </span>
                                </div>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-3">
                              {slide.narration || (
                                <span className="italic">No narration yet</span>
                              )}
                            </p>
                            <div className="flex gap-2 mt-3">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingSlide(index)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleMoveSlide(index, "up")}
                                disabled={index === 0}
                                className="touch-target"
                              >
                                <MoveUp className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleMoveSlide(index, "down")}
                                disabled={index === slides.length - 1}
                                className="touch-target"
                              >
                                <MoveDown className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDuplicateSlide(index)}
                                disabled={saving}
                                className="touch-target"
                                title="Duplicate slide"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteSlide(index)}
                                className="text-destructive touch-target"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="border-2 border-dashed rounded-lg p-6 sm:p-8">
                  <div className="max-w-md mx-auto text-center">
                    <Plus className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                    <h3 className="font-medium mb-4">Add New Slide</h3>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          className="hidden"
                          id="file-upload"
                        />
                        <label htmlFor="file-upload">
                          <Button variant="outline" asChild>
                            <span className="cursor-pointer">
                              <Plus className="h-4 w-4 mr-2" />
                              Upload Image
                            </span>
                          </Button>
                        </label>
                      </div>
                      <div>
                        <Button variant="outline" onClick={handleAddEmptySlide}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          Start with Text
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                      You can add either image or narration first
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={() => setCurrentStep(1)}>
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => setCurrentStep(3)}
                  // onClick={async () => {
                  //   await performAutoExport();
                  //   navigate('/dashboard');
                  // }}
                >
                  {/* Finish */}
                  Next
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === 3 && (
            <Card>
            <CardHeader>
              <CardTitle>Story Break</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleStoryBreakFileSelect}
              className="hidden"
            />

            {storyBreakPreview ? (
              <div className="flex flex-col items-center gap-4 border-2 border-dashed rounded-lg p-6">
                <img
                  src={storyBreakPreview}
                  alt="Story Break"
                  onClick={openFilePicker}
                  className="w-full sm:w-40 aspect-[393/620] object-cover rounded-md cursor-pointer hover:opacity-90 transition"
                />

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={openFilePicker}
                  >
                    Change Image
                  </Button>

                  <Button
                    type="button"
                    variant="destructive"
                    onClick={removeStoryBreak}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed rounded-lg p-6 sm:p-8">
                <div className="max-w-md mx-auto text-center">
                  <Plus className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />

                  <h3 className="font-medium mb-4">Add Story Break</h3>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={openFilePicker}
                    disabled={saving}
                  >
                    Upload Image
                  </Button>
                </div>
              </div>
            )}
          </>
          <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={() => setCurrentStep(2)}>
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button
                   disabled={saving}
                  onClick={async () => {
                    await performAutoExport();
                    navigate('/dashboard');
                  }}
                >
                  Finish
                </Button>
              </div>
          </CardContent>
          </Card>
        )}
      </main>

      {/* Inline creation modals */}
      {enableInlineCreation && (
        <>
          <CategoryCreateModal
            isOpen={showCategoryModal}
            onClose={() => setShowCategoryModal(false)}
            onCategoryCreated={handleCategoryCreated}
            type="category"
          />

          <CategoryCreateModal
            isOpen={showSubcategoryModal}
            onClose={() => setShowSubcategoryModal(false)}
            onCategoryCreated={handleSubcategoryModalCreated}
            type="subcategory"
            categoryId={storyData.category_id || undefined}
          />
        </>
      )}
    </div>
  );
}
